import {HTMLAttributes, ReactNode} from 'react';
import styled from 'styled-components';
import {typographyStyle} from '@/components/Typography';
import {MarkerStyleOptions} from '@/map/@types';
import {FlexCenter, interceptProps} from '@/styled-components';
import {palette} from '@/styles/palette';
import {getHexWithOpacity} from '@/styles/utils';
import {MarkerContentContainer} from './MarkerContentContainer';

export const DetailBox = styled.div`
  font-weight: 500;
  border-radius: 5px;
  padding: 8px;
  color: ${palette.white};
  background-color: ${getHexWithOpacity(palette.black, 0.6)};
  z-index: 1;
`;

export const CountBadge = styled(FlexCenter)`
  height: 16px;
  border-radius: 100px;
  padding: 1px 3px;
  font-weight: 500;
  ${{...typographyStyle.medium10}}
  color: ${palette.white};
  background-color: ${getHexWithOpacity(palette.black, 0.7)};
  box-sizing: border-box;
`;

type ArrowPositionType =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'left-center';

interface ArrowInterface {
  options?: MarkerStyleOptions;
  position?: ArrowPositionType;
  sideGap?: number; // xx-right, xx-left 포지션인 경우 사이드 끝점과의 거리 픽셀 (기본 6px)
}

export const Arrow = styled('div')
  .withConfig({
    shouldForwardProp: interceptProps(['options', 'position', 'sideGap']),
  })
  .attrs<ArrowInterface>(({position}) => ({
    className: position ?? 'bottom-left',
  }))<ArrowInterface>`
  position: absolute;
  width: 10px;
  height: 0px;
  z-index: 0;
  transition: all 400ms;
  &.bottom-left,
  &.bottom-right{
    bottom: 1px;
  }
  &.left-center {
    transform: rotate(90deg);
    right: calc(100% - 6px);
    top: 50%;
  }
  &.top-left,
  &.top-right {
    top: 1px;
    transform: rotate(180deg);
  }
  &.bottom-left,
  &.top-left {
    ${({sideGap = 6}) => `
    left: ${sideGap}px;
    `}
    &:before {
      left: 1px;
    }
    &:after {
      left: 0;
    }
  }
  &.bottom-right,
  &.top-right {
    ${({sideGap = 6}) => `
    right: ${sideGap}px;
    `}
    &:before {
      right: 1px;
    }
    &:after {
      right: 0;
    }
  }
  &:before,
  &:after {
    transition: all 400ms;
    content: '';
    display: block;
    position: absolute;
    width: 0;
    height: 0;
  }
  &:before {
    top: 100%;
    border: 3px solid transparent;
    border-top-width: 6px;
  }
  &:after {
    top: 100%;
    border: 4px solid transparent;
    border-top-width: 7px;
    z-index: -1;
  }
  &.left-center:after {
    transform: translateX(calc(-50% + 0px));
  }
  ${({options = {}}) => `
    &:before {
      border-top-color: ${
        options.default?.backgroundColor ?? palette.white
      } !important;
    }
    &:after {
      border-top-color: ${
        options.default?.borderColor ?? palette.gray400
      } !important;
    }
  `}
`;

export const Marker = styled('div').withConfig({
  shouldForwardProp: interceptProps(['options']),
})<{
  options?: MarkerStyleOptions;
}>`
  border-radius: 6px;
  padding: 4px;
  box-sizing: border-box;
  ${({options = {}}) => `
    background-color: ${
      options.default?.backgroundColor ?? palette.white
    } !important;
    border: solid 1px ${
      options.default?.borderColor ?? palette.gray400
    } !important;
    color: ${options.default?.color ?? palette.textPrimary} !important;
  `}
`;

export const Container = styled(
  ({
    options,
    ...props
  }: {
    options: MarkerStyleOptions;
    children?: ReactNode;
    anchor?: any;
  } & HTMLAttributes<HTMLDivElement>) => (
    <MarkerContentContainer
      anchor={{
        x: (options.adjustPixel ?? 0) + 0.5,
        y: -((options.adjustPixel ?? 0) + 7),
      }}
      {...props}
    />
  )
)`
  ${({options}) => options?.zIndex && `z-index: ${options.zIndex};`}
  &:hover {
    ${({options = {}}) =>
      !!options.hover &&
      `
      ${Arrow}, ${Marker} {
        background-color: ${options.hover?.backgroundColor} !important;
        border-color: ${options.hover?.borderColor} !important;
      }
      ${Arrow} {
        &:before {
          border-top-color: ${options.hover?.backgroundColor} !important;
        }
        &:after {
          border-top-color: ${options.hover?.borderColor} !important;
        }
      }
    `}
    ${({options}) => options?.zIndex && `z-index: ${options.zIndex + 10};`}
  }
`;
